<template>
  <div class="mission-ranking-list-section">
    <div class="mission-ranking-list-section__header">
      <mission-ranking-title-parts
        :title="pointRanking.title"
        :summary="pointRanking.summary"
        :rankingType="rankingType"
      />
      <div class="mission-ranking-list-section__period" v-if="pointRanking.aggregationPeriod">
        <mission-ranking-period-parts :periodData="pointRanking.aggregationPeriod" />
      </div>
    </div>
    <div class="mission-ranking-list-section__body">
      <mission-ranking-table-section
        :category="pointRanking.category"
        :rankings="pointRanking.rankings"
        :retrieveNameUsersByUserId="retrieveNameUsersByUserId"
        :rankingType="rankingType"
        @onClickSnsButton="onClickSnsButton"
        @onClickShareIcon="onClickShareIcon"
        @savePostSnsMissionHistory="$emit('savePostSnsMissionHistory')"
      />
    </div>
    <!-- 自分がランク外、かつ自分のランキングデータが存在する場合に表示する -->
    <div class="mission-ranking-list-section__self" v-if="isOwnRankUnranked && ownPointRanking">
      <mission-ranking-self-not-ranked-parts
        :selfData="ownPointRanking.selfData"
        :isOwnRank="ownPointRanking.isOwnRank"
        :retrieveNameUsersByUserId="retrieveNameUsersByUserId"
        :rankingType="rankingType"
        @onClickSnsButton="onClickSnsButton"
        @onClickShareIcon="onClickShareIcon"
        @savePostSnsMissionHistory="$emit('savePostSnsMissionHistory')"
      />
    </div>
    <div class="mission-ranking-list-section__footer" v-if="pointRanking.displayEndDate">
      <mission-ranking-end-date-parts :endDate="pointRanking.displayEndDate" />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import MissionRankingTitleParts from '@/components/MissionPage/MissionRankingPane/parts/MisstionRankingTitleParts.vue'
import MissionRankingTableSection from '@/components/MissionPage/MissionRankingPane/MissionRankingTableSection.vue'
import MissionRankingPeriodParts from '@/components/MissionPage/MissionRankingPane/parts/MissionRankingPeriodParts.vue'
import MissionRankingEndDateParts from '@/components/MissionPage/MissionRankingPane/parts/MissionRankingEndDateParts.vue'
import {
  PointRankingType,
  RankingType,
} from '@/store/stores/pageStore/MissionPage/MissionPageStore'
import { UserRetrieveNameResponseType } from '@/store/stores/collectionModule/documents/user/UserRetrieveNameDocument'
import StoreUtil from '@/store/StoreUtil'
import MissionRankingSelfNotRankedParts from '@/components/MissionPage/MissionRankingPane/parts/MissionRankingSelfNotRankedParts.vue'
import LoginStore from '@/store/stores/loginStore/LoginStore'
import useMissionRanking from '@/components/MissionPage/MissionRankingPane/hook/useMissionRanking'

export default defineComponent({
  name: 'MissionRankingListSection',
  components: {
    MissionRankingSelfNotRankedParts,
    MissionRankingEndDateParts,
    MissionRankingPeriodParts,
    MissionRankingTableSection,
    MissionRankingTitleParts,
  },
  props: {
    pointRanking: {
      type: Object as PropType<PointRankingType>,
      required: true,
    },
    retrieveNameUsersByUserId: {
      type: Object as PropType<Record<string, UserRetrieveNameResponseType>>,
    },
  },
  setup(props) {
    const contractInfoStore = StoreUtil.useStore('ContractInfoStore')
    const { ownOrganization } = contractInfoStore
    const { getOwnRankingData } = useMissionRanking()

    // 自分のランクがランク外である事を確認する
    const isOwnRankUnranked = computed(() => {
      if (!ownOrganization.value || !props.pointRanking.rankings) return undefined
      const ownUserId = LoginStore.value.userId || ''

      return !props.pointRanking.rankings.some((rank) => rank.userId === ownUserId)
    })

    // 自分のランキングデータをカテゴリーごとに出し分ける
    const ownPointRanking = computed(() =>
      getOwnRankingData(props.pointRanking, ownOrganization.value),
    )

    const rankingType = computed<RankingType>(() => {
      const pointRankingKey = props.pointRanking.key || ''
      const typeSeason = /.*SEASON.*/
      const typeMonthly = /.*MONTHLY.*/

      if (typeSeason.test(pointRankingKey)) {
        return 'SEASON'
      }

      if (typeMonthly.test(pointRankingKey)) {
        return 'MONTHLY'
      }

      return 'TOTAL'
    })

    return {
      isOwnRankUnranked,
      ownPointRanking,
      rankingType,
    }
  },
  methods: {
    onClickSnsButton() {
      const userData =
        this.ownPointRanking?.selfData ||
        this.pointRanking.rankings.find((rank) => rank.userId === LoginStore.value.userId)
      this.$emit('onClickSnsButton', userData, this.pointRanking)
    },
    onClickShareIcon() {
      const userData =
        this.ownPointRanking?.selfData ||
        this.pointRanking.rankings.find((rank) => rank.userId === LoginStore.value.userId)
      this.$emit('onClickShareIcon', userData, this.pointRanking)
    },
  },
})
</script>

<style scoped lang="scss">
.mission-ranking-list-section {
  padding: 12px 0 3px;
  background-color: rgba(#fff, 0.2);
  border: 1px solid rgba(#fff, 0.2);
  border-radius: 10px;
  backdrop-filter: blur(15px);

  &__header {
    padding: 0 10px;
  }

  &__body {
    margin-top: 16px;
  }

  &__footer {
    padding-top: 18px;
    padding-bottom: 15px;
    border-top: 1px solid $gray50;

    .mission-ranking-list-section__self + & {
      border-top: none;
    }
  }
}
</style>
